<template>
  <section class="cta-band" ref="ctaRoot" data-sticky-cta-hide>
    <div class="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-20">
      <div class="cta-panel" :class="{ 'cta-panel--plain': plain }">
        <div class="cta-copy max-w-2xl">
          <h2 class="cta-title text-2xl font-bold leading-tight tracking-normal sm:text-3xl md:text-4xl">
            {{ title || ctaCopy.title }}
          </h2>
          <p class="cta-body mt-5 max-w-xl text-base leading-7 tracking-normal">
            {{ body || ctaCopy.body }}
          </p>

          <div class="cta-actions mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              class="cta-btn cta-btn--primary inline-flex min-h-12 items-center justify-center px-6 text-sm font-semibold"
              :href="addReferralToUrl(signupUrl || 'https://app.workspace369.com/signup')"
              data-conversion-event="start_free_click"
              :data-conversion-label="conversionLabel || 'Global CTA primary'"
              data-conversion-location="global-cta"
            >
              {{ ctaCopy.primary }}
            </a>
            <NuxtLink
              class="cta-btn cta-btn--secondary inline-flex min-h-12 items-center justify-center px-6 text-sm font-semibold"
              :to="localePath('/contact-sales')"
              data-conversion-event="demo_request_click"
              data-conversion-label="Global CTA demo"
              data-conversion-location="global-cta"
            >
              {{ ctaCopy.secondary }}
            </NuxtLink>
          </div>
          <TrialDisclosure :plan="signupPlan" />
        </div>

        <div v-if="!plain" class="cta-preview" aria-hidden="true">
          <div class="preview-topbar">
            <span></span>
            <span></span>
            <span></span>
            <strong>{{ ctaCopy.previewTitle }}</strong>
          </div>
          <img
            src="/home/screens/invoice-edit-light-clean.webp"
            :alt="tr('Workspace369 invoice editor with billing details, line items, and a live invoice preview showing amount due and Pay Now')"
            loading="lazy"
            width="2732"
            height="1467"
          />
          <div class="preview-strip">
            <div v-for="item in highlights" :key="item.label">
              <strong>{{ item.value }}</strong>
              <span>{{ item.label }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
const { addReferralToUrl } = useReferral();
const { localePath } = useWorkspaceLocale();
const homeCopy = useHomeCopy();
const { tr } = useHomeI18n();

// A story page can give the closing band its own line and copy, and drop the stock product
// screenshot (`plain`) so the band closes on the page's own subject.
const props = defineProps({
  title: { type: String, default: "" },
  body: { type: String, default: "" },
  plain: { type: Boolean, default: false },
  signupUrl: { type: String, default: "" },
  // Tags the trial click with its page (e.g. a release note), so sign-ups can be counted per page.
  conversionLabel: { type: String, default: "" },
});
// The trial line names the plan the button opens (`?plan=` on signupUrl).
const signupPlan = computed(() => {
  try {
    return new URL(props.signupUrl).searchParams.get("plan") || "";
  } catch {
    return "";
  }
});
const ctaCopy = computed(() => homeCopy.value.cta);
const highlights = computed(() => ctaCopy.value.highlights);

const ctaRoot = ref(null);
useTiltFrames(ctaRoot, ".cta-preview", 3.5);
</script>

<style scoped>
.cta-panel {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 2rem;
  align-items: center;
  overflow: hidden;
  border: 1px solid var(--cta-line);
  border-radius: 0.625rem;
  background: var(--cta-surface);
  padding: 1.25rem;
}

.cta-preview {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--cta-line);
  border-radius: 0.625rem;
  background: var(--cta-surface);
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.06);
  transition:
    transform 200ms ease,
    box-shadow 200ms ease,
    border-color 200ms ease;
}

.cta-preview::after {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    560px circle at var(--mx, 50%) var(--my, 50%),
    rgb(0 0 0 / 0.04),
    transparent 42%
  );
  content: "";
  opacity: 0;
  pointer-events: none;
  transition: opacity 200ms ease;
}

.cta-preview:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgb(0 0 0 / 0.12);
}

.cta-preview.tilt-enabled {
  transition:
    box-shadow 200ms ease,
    border-color 200ms ease;
}

.cta-preview.is-tilting {
  transform: perspective(1100px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg)) translateY(-4px);
  box-shadow: 0 8px 24px rgb(0 0 0 / 0.12);
}

.cta-preview.is-tilting::after {
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .cta-preview {
    transition: none;
  }

  .cta-preview:hover,
  .cta-preview.is-tilting {
    transform: none;
  }
}

.preview-topbar {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  border-bottom: 1px solid var(--cta-line);
  padding: 0.72rem 0.85rem;
}

.preview-topbar span {
  height: 0.52rem;
  width: 0.52rem;
  border-radius: 999px;
  background: var(--cta-line);
}

.preview-topbar strong {
  margin-left: 0.3rem;
  color: var(--cta-muted);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0;
}

.cta-preview img {
  display: block;
  height: auto;
  width: 100%;
}

.preview-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border-top: 1px solid var(--cta-line);
}

.preview-strip div {
  min-width: 0;
  padding: 0.85rem;
}

.preview-strip div + div {
  border-left: 1px solid var(--cta-line);
}

.preview-strip strong {
  display: block;
  color: var(--cta-ink);
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0;
}

.preview-strip span {
  display: block;
  margin-top: 0.18rem;
  color: var(--cta-muted);
  font-size: 0.68rem;
  line-height: 1.35;
  letter-spacing: 0;
}

@media (min-width: 768px) {
  .cta-panel {
    grid-template-columns: minmax(0, 0.92fr) minmax(20rem, 1.08fr);
    gap: 2.5rem;
    padding: 2rem;
  }
}

@media (min-width: 1024px) {
  .cta-panel {
    padding: 2.5rem;
  }
}

/* On a phone the desktop screenshot is too small to read; the buttons carry the section. */
@media (max-width: 640px) {
  .cta-preview {
    display: none;
  }
}
.cta-panel--plain { grid-template-columns: minmax(0, 1fr) !important; padding: 2.5rem 1.5rem; }
@media (min-width: 768px) { .cta-panel--plain { padding: 3.5rem; } }

.cta-band { background: var(--cta-bg); }
/* On a phone at 125% text a long word (Workspace369, German and Finnish compounds) is wider than the panel at 3xl:
   the title steps down below sm, and a word that still can't fit wraps instead of running under the clip. */
.cta-title { margin-top: 0; color: var(--cta-ink); overflow-wrap: break-word; }
/* Narrow phones at large text: keep "Workspace369" on one line */
@media (max-width: 359px) { .cta-title { font-size: min(1.5rem, 8.6vw); } }
.cta-body { color: var(--cta-muted); }
/* The app's buttons are 6px; the primary is the app's #171717 (inverted to near-white in dark). */
.cta-btn { border-radius: 0.375rem; transition: background-color 150ms ease, border-color 150ms ease; }
.cta-btn--primary { background: var(--cta-primary); color: var(--cta-primary-ink); border: 1px solid var(--cta-primary); }
.cta-btn--primary:hover { background: var(--cta-primary-hover); border-color: var(--cta-primary-hover); }
.cta-btn--secondary { border: 1px solid var(--cta-line-strong); color: var(--cta-ink); background: transparent; }
.cta-btn--secondary:hover { background: var(--cta-hover); }
.cta-btn:focus-visible { outline: 2px solid var(--cta-ink); outline-offset: 2px; }

/* Below sm the buttons stack, which put the demo button between the trial button and its terms.
   Unwrap the row so the terms sit directly under the trial button (legal B1.1); sm and up keep the row. */
@media (max-width: 639px) {
  .cta-copy { display: flex; flex-direction: column; }
  .cta-actions { display: contents; }
  .cta-btn--primary { order: 1; margin-top: 2rem; }
  .cta-copy :deep(.trial-disclosure) { order: 2; }
  .cta-btn--secondary { order: 3; margin-top: 0.75rem; }
}
</style>

<!-- Theme tokens for the closing band: the app's neutral palette (white / #171717 in light, near-black in dark),
     no navy and no teal. The html element carries the `dark` class. -->
<style>
.cta-band {
  --cta-bg: #fafafa; --cta-surface: #ffffff; --cta-line: #e6e6e6; --cta-line-strong: #8a8a8a;
  --cta-ink: #171717; --cta-muted: #525252; --cta-hover: #f3f3f3;
  --cta-primary: #171717; --cta-primary-hover: #2e2e2e; --cta-primary-ink: #ffffff;
}
.dark .cta-band {
  --cta-bg: #141414; --cta-surface: #1c1c1c; --cta-line: #2e2e2e; --cta-line-strong: #6b6b6b;
  --cta-ink: #fafafa; --cta-muted: #a3a3a3; --cta-hover: #262626;
  --cta-primary: #fafafa; --cta-primary-hover: #e5e5e5; --cta-primary-ink: #171717;
}
</style>
